import { supabase } from '../lib/supabase';
import { getCurrentUserProfile } from './usuariosService';

// Buscar todas as funções de usuários
export const getAllFuncoes = async () => {
  try {
    console.log('🔄 Buscando funções de usuários...');
    
    const { data, error } = await supabase
      .from('funcoes_usuarios')
      .select('*')
      .order('nivel_acesso', { ascending: false });

    if (error) {
      console.error('❌ Erro ao buscar funções:', error);
      throw new Error(error.message);
    }

    console.log('✅ Funções encontradas:', data?.length || 0);
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('❌ Erro geral ao buscar funções:', error);
    return { success: false, error: error.message };
  }
};

// Buscar função por ID
export const getFuncaoById = async (id) => {
  try {
    console.log('🔄 Buscando função por ID:', id);
    
    const { data, error } = await supabase
      .from('funcoes_usuarios')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('❌ Erro ao buscar função:', error);
      throw new Error(error.message);
    }

    console.log('✅ Função encontrada:', data.nome);
    return { success: true, data };
  } catch (error) {
    console.error('❌ Erro geral ao buscar função:', error);
    return { success: false, error: error.message };
  }
};

// Criar nova função
export const createFuncao = async (funcaoData) => {
  try {
    console.log('🔄 Criando função:', funcaoData.nome);
    
    const { data, error } = await supabase
      .from('funcoes_usuarios')
      .insert([{
        nome: funcaoData.nome,
        descricao: funcaoData.descricao || '',
        cor: funcaoData.cor || '#3b82f6',
        icone: funcaoData.icone || 'Shield',
        nivel_acesso: funcaoData.nivel_acesso || 1,
        permissoes: funcaoData.permissoes || [],
        is_active: funcaoData.is_active !== undefined ? funcaoData.is_active : true,
        is_system: false // Funções criadas pelo usuário nunca são do sistema
      }])
      .select()
      .single();

    if (error) {
      console.error('❌ Erro ao criar função:', error);
      throw new Error(error.message);
    }

    console.log('✅ Função criada:', data.nome);
    return { success: true, data };
  } catch (error) {
    console.error('❌ Erro geral ao criar função:', error);
    return { success: false, error: error.message };
  }
};

// Atualizar função
export const updateFuncao = async (id, funcaoData) => {
  try {
    console.log('🔄 Atualizando função:', id);
    
    const updateData = {
      nome: funcaoData.nome,
      descricao: funcaoData.descricao || '',
      cor: funcaoData.cor || '#3b82f6',
      icone: funcaoData.icone || 'Shield',
      nivel_acesso: funcaoData.nivel_acesso || 1,
      permissoes: funcaoData.permissoes || [],
      is_active: funcaoData.is_active !== undefined ? funcaoData.is_active : true,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('funcoes_usuarios')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('❌ Erro ao atualizar função:', error);
      throw new Error(error.message);
    }

    console.log('✅ Função atualizada:', data.nome);
    return { success: true, data };
  } catch (error) {
    console.error('❌ Erro geral ao atualizar função:', error);
    return { success: false, error: error.message };
  }
};

// Alternar status ativo/inativo
export const toggleFuncaoStatus = async (id, isActive) => {
  try {
    console.log('🔄 Alterando status da função:', id, 'para', isActive ? 'ativo' : 'inativo');
    
    const { data, error } = await supabase
      .from('funcoes_usuarios')
      .update({ 
        is_active: isActive,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('❌ Erro ao alterar status:', error);
      throw new Error(error.message);
    }

    console.log('✅ Status alterado:', data.nome, '-', isActive ? 'ativo' : 'inativo');
    return { success: true, data };
  } catch (error) {
    console.error('❌ Erro geral ao alterar status:', error);
    return { success: false, error: error.message };
  }
};

// Deletar função (apenas não-sistema)
export const deleteFuncao = async (id) => {
  try {
    console.log('🔄 Deletando função:', id);
    
    // Primeiro verificar se é função do sistema
    const { data: funcao, error: fetchError } = await supabase
      .from('funcoes_usuarios')
      .select('nome, is_system, usuarios_count')
      .eq('id', id)
      .single();

    if (fetchError) {
      console.error('❌ Função não encontrada:', fetchError);
      throw new Error('Função não encontrada');
    }

    if (funcao.is_system) {
      throw new Error('Funções do sistema não podem ser excluídas');
    }

    if (funcao.usuarios_count > 0) {
      throw new Error(`Não é possível excluir a função "${funcao.nome}" pois há ${funcao.usuarios_count} usuário(s) associado(s)`);
    }

    const { error: deleteError } = await supabase
      .from('funcoes_usuarios')
      .delete()
      .eq('id', id);
    
    if (deleteError) {
      console.error('❌ Erro ao deletar função:', deleteError);
      throw new Error(deleteError.message);
    }

    console.log('✅ Função deletada:', funcao.nome);
    return { success: true };
  } catch (error) {
    console.error('❌ Erro geral ao deletar função:', error);
    return { success: false, error: error.message };
  }
};

// Buscar com filtros
export const searchFuncoes = async (searchTerm = '', filters = {}) => {
  try {
    console.log('🔍 Buscando funções com filtros:', { searchTerm, filters });
    
    let query = supabase
      .from('funcoes_usuarios')
      .select('*');

    // Filtro de busca por nome ou descrição
    if (searchTerm) {
      query = query.or(`nome.ilike.%${searchTerm}%,descricao.ilike.%${searchTerm}%`);
    }

    // Filtro por status
    if (filters.is_active !== undefined) {
      query = query.eq('is_active', filters.is_active);
    }

    // Filtro por tipo (sistema ou customizada)
    if (filters.is_system !== undefined) {
      query = query.eq('is_system', filters.is_system);
    }

    // Filtro por nível de acesso
    if (filters.nivel_min) {
      query = query.gte('nivel_acesso', filters.nivel_min);
    }
    if (filters.nivel_max) {
      query = query.lte('nivel_acesso', filters.nivel_max);
    }

    // Ordenação
    query = query.order('nivel_acesso', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('❌ Erro na busca:', error);
      throw new Error(error.message);
    }

    console.log('✅ Busca concluída:', data?.length || 0, 'resultados');
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('❌ Erro geral na busca:', error);
    return { success: false, error: error.message };
  }
};

// Obter estatísticas das funções
export const getFuncoesStats = async () => {
  try {
    console.log('📊 Calculando estatísticas das funções...');
    
    // Buscar perfil do usuário para aplicar filtro school_adm
    const profileResult = await getCurrentUserProfile();
    let query = supabase
      .from('funcoes_usuarios')
      .select('is_active, is_system, usuarios_count, nivel_acesso');

    const { data, error } = await query;

    if (error) {
      console.error('❌ Erro ao calcular estatísticas:', error);
      throw new Error(error.message);
    }

    // Calcular contagem de usuários filtrada por escola se for school_adm
    let totalUsers = 0;
    if (profileResult.success && profileResult.data?.school_adm && profileResult.data?.school_id) {
      // Para school admin, contar apenas usuários da sua escola
      console.log('📋 QUERY COMPLETO PARA SCHOOL ADMIN:');
      console.log(`SELECT COUNT(*) FROM profiles WHERE school_id = '${profileResult.data.school_id}'`);
      
      const { count, error: usersError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('school_id', profileResult.data.school_id);
      
      console.log('🔍 School admin count:', count, 'school_id:', profileResult.data.school_id);
      console.log('🔍 Error:', usersError);
      if (!usersError) {
        totalUsers = count || 0;
      }
    } else {
      // Para super admin, usar contagem total
      console.log('📋 QUERY COMPLETO PARA SUPER ADMIN:');
      console.log('SELECT is_active, is_system, usuarios_count, nivel_acesso FROM funcoes_usuarios');
      console.log('🔍 Somando usuarios_count de todas as funções:', data.map(f => f.usuarios_count));
      totalUsers = data.reduce((sum, f) => sum + (f.usuarios_count || 0), 0);
      console.log('🔍 Super admin total:', totalUsers);
    }

    const stats = {
      total: data.length,
      active: data.filter(f => f.is_active).length,
      inactive: data.filter(f => !f.is_active).length,
      system: data.filter(f => f.is_system).length,
      custom: data.filter(f => !f.is_system).length,
      total_users: totalUsers,
      avg_level: Math.round(data.reduce((sum, f) => sum + f.nivel_acesso, 0) / data.length * 10) / 10
    };

    console.log('✅ Estatísticas calculadas:', stats);
    return { success: true, data: stats };
  } catch (error) {
    console.error('❌ Erro geral ao calcular estatísticas:', error);
    return { success: false, error: error.message };
  }
};

// Obter usuários por função
export const getUsersByFuncao = async (funcaoId) => {
  try {
    console.log('👥 Buscando usuários da função:', funcaoId);
    
    // Buscar perfil do usuário para aplicar filtro school_adm
    const profileResult = await getCurrentUserProfile();
    let query = supabase
      .from('profiles')
      .select('id, name, email, department, is_active, created_at, school_id')
      .eq('funcao_id', funcaoId);

    // Se for school admin, filtrar apenas usuários da sua escola
    if (profileResult.success && profileResult.data?.school_adm && profileResult.data?.school_id) {
      query = query.eq('school_id', profileResult.data.school_id);
    }

    const { data, error } = await query.order('name');

    if (error) {
      console.error('❌ Erro ao buscar usuários:', error);
      throw new Error(error.message);
    }

    console.log('✅ Usuários encontrados:', data?.length || 0);
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('❌ Erro geral ao buscar usuários:', error);
    return { success: false, error: error.message };
  }
};

// Duplicar função
export const duplicateFuncao = async (id) => {
  try {
    console.log('📋 Duplicando função:', id);
    
    // Buscar função original
    const { data: original, error: fetchError } = await supabase
      .from('funcoes_usuarios')
      .select('*')
      .eq('id', id)
      .single();

    if (fetchError) {
      throw new Error('Função não encontrada');
    }

    // Criar cópia
    const copyData = {
      nome: `${original.nome} (Cópia)`,
      descricao: original.descricao,
      cor: original.cor,
      icone: original.icone,
      nivel_acesso: original.nivel_acesso,
      permissoes: original.permissoes,
      is_active: true,
      is_system: false // Cópias nunca são do sistema
    };

    const result = await createFuncao(copyData);
    
    if (result.success) {
      console.log('✅ Função duplicada:', result.data.nome);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Erro geral ao duplicar função:', error);
    return { success: false, error: error.message };
  }
};

// Lista de permissões disponíveis no sistema
export const getPermissoesDisponiveis = () => {
  return [
    // Administração
    { key: 'admin.full', label: 'Administração Total', category: 'Administração' },
    { key: 'admin.users', label: 'Gerenciar Usuários', category: 'Administração' },
    { key: 'admin.system', label: 'Configurações do Sistema', category: 'Administração' },
    
    // Escolas
    { key: 'schools.manage', label: 'Gerenciar Escolas', category: 'Escolas' },
    { key: 'schools.view', label: 'Visualizar Escolas', category: 'Escolas' },
    
    // Usuários
    { key: 'users.manage', label: 'Gerenciar Usuários', category: 'Usuários' },
    { key: 'users.view', label: 'Visualizar Usuários', category: 'Usuários' },
    { key: 'users.school', label: 'Usuários da Escola', category: 'Usuários' },
    
    // Alunos
    { key: 'students.manage', label: 'Gerenciar Alunos', category: 'Alunos' },
    { key: 'students.view', label: 'Visualizar Alunos', category: 'Alunos' },
    { key: 'students.enrollment', label: 'Matrículas', category: 'Alunos' },
    
    // Testes e Avaliações
    { key: 'tests.manage', label: 'Gerenciar Testes', category: 'Avaliações' },
    { key: 'tests.create', label: 'Criar Testes', category: 'Avaliações' },
    { key: 'tests.view', label: 'Visualizar Testes', category: 'Avaliações' },
    { key: 'grades.manage', label: 'Gerenciar Notas', category: 'Avaliações' },
    
    // Conteúdos
    { key: 'content.manage', label: 'Gerenciar Conteúdos', category: 'Conteúdos' },
    { key: 'content.view', label: 'Visualizar Conteúdos', category: 'Conteúdos' },
    { key: 'content.upload', label: 'Upload de Arquivos', category: 'Conteúdos' },
    
    // Relatórios
    { key: 'reports.view', label: 'Visualizar Relatórios', category: 'Relatórios' },
    { key: 'reports.advanced', label: 'Relatórios Avançados', category: 'Relatórios' },
    { key: 'reports.export', label: 'Exportar Relatórios', category: 'Relatórios' },
    
    // Conversas e Chat
    { key: 'chat.access', label: 'Acesso ao Chat', category: 'Comunicação' },
    { key: 'chat.moderate', label: 'Moderar Conversas', category: 'Comunicação' },
    
    // Básicas
    { key: 'basic.view', label: 'Visualização Básica', category: 'Básico' },
    { key: 'basic.reports', label: 'Relatórios Básicos', category: 'Básico' }
  ];
};

// Lista de ícones disponíveis
export const getIconesDisponiveis = () => {
  return [
    'Shield', 'Crown', 'Building', 'GraduationCap', 'BookOpen', 
    'FileText', 'Eye', 'User', 'Users', 'UserCheck', 'Settings',
    'Star', 'Award', 'Badge', 'Key', 'Lock', 'Unlock'
  ];
};
